import _ from "lodash";

import { CustomFieldSourceType, FieldType } from "prisma/cm/client";

import { CustomFieldValue, CustomFieldValuesMap } from "./types";

export type CustomFieldable = {
  customFieldValues: CustomFieldValuesMap;
};

export function isComputedCustomField<
  T extends {
    sourceType: CustomFieldSourceType;
  },
>(customField: T) {
  return customField.sourceType === CustomFieldSourceType.COMPUTED;
}

export function getCustomFieldData<T extends CustomFieldable>(
  customFieldable: T | null,
  fieldId: string,
) {
  if (!customFieldable) {
    return null;
  }

  return customFieldable?.customFieldValues?.[fieldId];
}

export function getCustomFieldValue<T extends CustomFieldable>(
  customFieldable: T | null,
  fieldId: string,
) {
  if (!customFieldable) {
    return null;
  }

  return getCustomFieldData(customFieldable, fieldId)?.actualValue;
}

export function getCustomFieldSuggestion<T extends CustomFieldable>(
  customFieldable: T | null,
  fieldId: string,
) {
  return getCustomFieldData(customFieldable, fieldId)?.logicComputedValue;
}

export function updateCustomFieldPiece<T extends CustomFieldable>({
  customFieldable,
  fieldId,
  newValue,
  key,
}: {
  customFieldable: T;
  fieldId: string;
  newValue: any;
  key: keyof CustomFieldValue;
}) {
  const existingCustomFieldData = getCustomFieldData(customFieldable, fieldId) ?? {
    actualValue: null,
  };

  const newCustomFieldValue: CustomFieldValue = {
    ...existingCustomFieldData,
    [key]: newValue,
  };

  customFieldable.customFieldValues = {
    ...customFieldable.customFieldValues,
    [fieldId]: newCustomFieldValue,
  };

  return customFieldable;
}

export function updateCustomFieldValue<T extends CustomFieldable>({
  customFieldable,
  fieldId,
  newValue,
  manuallyEntered = false,
}: {
  customFieldable: T;
  fieldId: string;
  newValue: any;
  manuallyEntered?: boolean;
}) {
  let object = updateCustomFieldPiece({
    customFieldable,
    fieldId,
    newValue,
    key: "actualValue",
  });

  if (manuallyEntered) {
    object = updateCustomFieldPiece({
      customFieldable: object,
      fieldId,
      newValue: true,
      key: "isManuallyEdited",
    });
  }

  return object;
}

export function coerceImportCustomFieldValueToDbFormat(value: string, type: FieldType) {
  if (value === "") {
    return null;
  }

  switch (type) {
    case FieldType.TEXT:
      return value;
    case FieldType.DATE:
      return value;
    case FieldType.NUMBER:
      return Number(value);
    case FieldType.PERCENT: {
      let newValue = value;
      // Need to drop percent sign off end of value
      if (isNaN(Number(value[value.length - 1]))) {
        newValue = newValue.slice(0, -1);
      }

      return Number(newValue);
    }
    case FieldType.DOLLAR:
      const safeValue = _.isString(value) ? value.replace(/,/g, "") : value;
      return Number(safeValue) * 100;
    default:
      throw new Error(`Unsupported fieldtype for custom column importing: ${type}`);
  }
}
